/**
 * Arthaus Discovery Graph-Walk
 * Phase: 9 (feature c)
 *
 * Turns any product tile's "More like this" button into an in-page, walkable
 * exploration. Calls the Picasso concierge /api/recommend (dimensions mode) and
 * renders swim lanes (More-from-artist → visual → concept threads). The shopper:
 *   • taps an artwork  → re-seeds the walk around it (lanes recompute)
 *   • follows a thread → biases the next recompute toward that graph facet
 *   • uses the breadcrumb / back → returns to an earlier step
 * State persists to sessionStorage so a mid-walk reload resumes. Pure progressive
 * enhancement: the underlying grid and PDP links work without any of this.
 */
(function () {
  'use strict';

  var SS_KEY = 'arthaus_walk_v1';

  function esc(s) {
    return (s == null ? '' : String(s)).replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  var CONCEPT_KINDS = { Concept: 1, Theme: 1, Style: 1, Entity: 1 };

  function Walk(root) {
    this.root = root;
    this.api = (root.getAttribute('data-api') || '').replace(/\/$/, '');
    this.panel = root.querySelector('[data-dd-panel]');
    this.overlay = root.querySelector('[data-dd-overlay]');
    this.lanesEl = root.querySelector('[data-dd-lanes]');
    this.crumbEl = root.querySelector('[data-dd-crumb]');
    this.narrEl = root.querySelector('[data-dd-narration]');
    this.loadEl = root.querySelector('[data-dd-loading]');
    this.emptyEl = root.querySelector('[data-dd-empty]');
    this.backEl = root.querySelector('[data-dd-back]');
    this.bodyEl = root.querySelector('[data-dd-body]');
    this.stack = [];       // frames: {handle, title, biasKey, biasLabel, label}
    this.reqSeq = 0;
    this.bind();
  }

  Walk.prototype.bind = function () {
    var self = this;
    // Global launcher (event delegation — tiles render after this script).
    document.addEventListener('click', function (e) {
      var t = e.target.closest && e.target.closest('[data-mlt-handle]');
      if (!t) return;
      e.preventDefault();
      self.start(t.getAttribute('data-mlt-handle'), t.getAttribute('data-mlt-title') || '');
    });
    this.overlay.addEventListener('click', function () { self.close(); });
    this.root.querySelector('[data-dd-close]').addEventListener('click', function () { self.close(); });
    this.backEl.addEventListener('click', function () { self.back(); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.isOpen()) self.close();
    });
    // Delegated actions inside the drawer.
    this.lanesEl.addEventListener('click', function (e) {
      var rs = e.target.closest && e.target.closest('[data-reseed-handle]');
      if (rs) { self.push({ handle: rs.getAttribute('data-reseed-handle'), title: rs.getAttribute('data-reseed-title') || '', label: rs.getAttribute('data-reseed-title') || 'this piece' }); return; }
      var fl = e.target.closest && e.target.closest('[data-follow-key]');
      if (fl) { var cur = self.current(); self.push({ handle: cur.handle, title: cur.title, biasKey: fl.getAttribute('data-follow-key'), biasLabel: fl.getAttribute('data-follow-label') || '', label: fl.getAttribute('data-follow-label') || 'this thread' }); return; }
    });
    this.crumbEl.addEventListener('click', function (e) {
      var c = e.target.closest && e.target.closest('[data-crumb-idx]');
      if (!c) return;
      var idx = parseInt(c.getAttribute('data-crumb-idx'), 10);
      if (idx >= 0 && idx < self.stack.length - 1) { self.stack = self.stack.slice(0, idx + 1); self.render(); }
    });
  };

  Walk.prototype.isOpen = function () { return !this.root.classList.contains('hidden'); };
  Walk.prototype.current = function () { return this.stack[this.stack.length - 1] || {}; };

  Walk.prototype.open = function () {
    if (this.isOpen()) return;
    var self = this;
    this.root.classList.remove('hidden');
    document.documentElement.style.overflow = 'hidden';
    // next frame → run the slide/fade-in transition
    requestAnimationFrame(function () {
      self.overlay.classList.remove('opacity-0');
      self.panel.classList.remove('translate-y-full', 'lg:translate-x-full');
    });
  };

  Walk.prototype.close = function () {
    if (!this.isOpen()) return;
    var self = this;
    this.overlay.classList.add('opacity-0');
    this.panel.classList.add('translate-y-full', 'lg:translate-x-full');
    document.documentElement.style.overflow = '';
    setTimeout(function () { self.root.classList.add('hidden'); }, 300);
  };

  Walk.prototype.start = function (handle, title) {
    this.stack = [{ handle: handle, title: title, label: title || 'this piece' }];
    this.open();
    this.render();
  };
  Walk.prototype.push = function (frame) {
    // No-op if re-seeding the same place with no new bias.
    var cur = this.current();
    if (cur.handle === frame.handle && !frame.biasKey && !cur.biasKey) { return; }
    this.stack.push(frame);
    this.bodyEl.scrollTop = 0;
    this.render();
  };
  Walk.prototype.back = function () {
    if (this.stack.length <= 1) return;
    this.stack.pop();
    this.render();
  };

  Walk.prototype.renderCrumb = function () {
    var html = this.stack.map(function (f, i) {
      var sep = i ? '<span class="text-mute">›</span>' : '';
      var isLast = i === this.stack.length - 1;
      var cls = isLast ? 'text-charcoal' : 'text-warm-gray hover:text-bronze cursor-pointer';
      var inner = '<span data-crumb-idx="' + i + '" class="' + cls + ' truncate max-w-[160px] inline-block align-bottom">' + esc(f.label) + '</span>';
      return sep + inner;
    }, this).join(' ');
    this.crumbEl.innerHTML = html;
    this.backEl.classList.toggle('hidden', this.stack.length <= 1);
  };

  Walk.prototype.tile = function (p) {
    if (!p.handle) return '';
    var pdp = '/products/' + p.handle;
    return '<div class="snap-start shrink-0 w-[150px] lg:w-[160px]">' +
      '<button type="button" data-reseed-handle="' + esc(p.handle) + '" data-reseed-title="' + esc(p.title) + '" class="group block w-full text-left" aria-label="Explore from ' + esc(p.title) + '">' +
        '<div class="aspect-[3/4] rounded-lg overflow-hidden bg-parchment mb-2 relative">' +
          (p.image ? '<img src="' + esc(p.image) + '" alt="' + esc(p.title) + '" loading="lazy" width="160" height="213" class="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500">' : '') +
          '<span class="absolute inset-0 bg-charcoal/0 group-hover:bg-charcoal/10 transition-colors"></span>' +
        '</div>' +
        '<h4 class="font-display text-sm text-charcoal font-light leading-snug line-clamp-2 group-hover:text-bronze transition-colors">' + esc(p.title) + '</h4>' +
        (p.artist ? '<p class="font-body text-xs text-mute truncate">' + esc(p.artist) + '</p>' : '') +
      '</button>' +
      '<a href="' + pdp + '" class="inline-block mt-1 font-body text-xs text-bronze hover:underline">View →</a>' +
    '</div>';
  };

  Walk.prototype.lane = function (d) {
    var tiles = (d.products || []).map(this.tile, this).filter(Boolean).join('');
    if (!tiles) return '';
    var follow = CONCEPT_KINDS[d.kind]
      ? '<button type="button" data-follow-key="' + esc(d.key) + '" data-follow-label="' + esc(d.label) + '" class="shrink-0 font-body text-xs text-bronze hover:underline">Follow this thread →</button>'
      : '';
    return '<section class="mb-9" data-dd-lane>' +
        '<div class="flex items-baseline justify-between gap-3 mb-1">' +
          '<h3 class="font-display text-lg lg:text-xl text-charcoal font-light tracking-tight">' + esc(d.label) + '</h3>' +
          '<span class="font-mono text-[11px] text-mute uppercase tracking-wider shrink-0">' + esc(d.kind) + '</span>' +
        '</div>' +
        (d.blurb ? '<p class="font-body text-[13px] text-warm-gray leading-relaxed mb-3 max-w-xl">' + esc(d.blurb) + '</p>' : '<div class="mb-3"></div>') +
        '<div class="flex overflow-x-auto snap-x gap-4 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">' + tiles + '</div>' +
        (follow ? '<div class="mt-1">' + follow + '</div>' : '') +
      '</section>';
  };

  Walk.prototype.render = function () {
    this.persist();
    this.renderCrumb();
    var f = this.current();
    if (!f.handle) return;
    var self = this;
    var seq = ++this.reqSeq;

    this.narrEl.textContent = '';
    this.emptyEl.classList.add('hidden');
    this.loadEl.classList.remove('hidden');

    var body = {
      cart_handles: [f.handle],
      seed_titles: f.title ? [f.title] : [],
      dimensions: 3,
      per_lane: 8,
      default_lanes: true,
      concierge: true
    };
    if (f.biasKey) body.bias_facets = [f.biasKey];

    fetch(this.api + '/api/recommend', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Arthaus-Client': 'storefront' },
      body: JSON.stringify(body)
    })
      .then(function (r) { if (!r.ok) throw new Error('recommend ' + r.status); return r.json(); })
      .then(function (d) {
        if (seq !== self.reqSeq) return; // a newer pivot superseded this one
        self.loadEl.classList.add('hidden');
        var html = (d.dimensions || []).map(self.lane, self).filter(Boolean).join('');
        self.lanesEl.innerHTML = html;
        var msg = d.concierge && d.concierge.message;
        self.narrEl.textContent = msg || '';
        self.emptyEl.classList.toggle('hidden', !!html);
      })
      .catch(function () {
        if (seq !== self.reqSeq) return;
        self.loadEl.classList.add('hidden');
        self.emptyEl.classList.remove('hidden');
      });
  };

  Walk.prototype.persist = function () {
    try { sessionStorage.setItem(SS_KEY, JSON.stringify(this.stack)); } catch (e) { /* ignore */ }
  };
  Walk.prototype.restore = function () {
    try {
      var s = JSON.parse(sessionStorage.getItem(SS_KEY) || '[]');
      if (Array.isArray(s) && s.length && s[0].handle) this.stack = s;
    } catch (e) { /* ignore */ }
  };

  function init() {
    var root = document.querySelector('[data-discovery-drawer]');
    if (!root || root._walk) return;
    root._walk = new Walk(root);
    root._walk.restore(); // keeps breadcrumb across a same-session reload; opens only on a fresh click
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
